// src/services/operationCenter/WidgetSchemaService.ts
// Shared widget schema definitions for AI service and widget registry

import type { WidgetType, WidgetCategory } from "@/types/dashboard";
// import { SIGNAL_DEFINITIONS } from "@/constants/signals";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface WidgetSchemaEntry {
  type: WidgetType;
  label: string;
  description: string;
  category: WidgetCategory;
  defaultConfig: Record<string, unknown>;
  configHints?: string; // Human-readable hints for AI about valid config values
}

// ─────────────────────────────────────────────────────────────────────────────
// Widget Schema Definitions
// Single source of truth for widget metadata used by AI and registry
// ─────────────────────────────────────────────────────────────────────────────

export const WIDGET_SCHEMA: WidgetSchemaEntry[] = [
  // Market Intelligence
  {
    type: "top_movers",
    label: "Top Movers",
    description: "Display top gainers or losers by price change",
    category: "market",
    defaultConfig: { direction: "gainers", time_range: "1d", limit: 5 },
    configHints:
      "direction: 'gainers' | 'losers'; time_range: '1d' | '7d' | '30d' | '365d'; limit: 3-20",
  },
  {
    type: "watchlist",
    label: "Watchlist",
    description: "Track your favorite tokens",
    category: "market",
    defaultConfig: { tickers: ["BTC", "ETH", "SOL"] },
    configHints: "tickers: array of uppercase token symbols",
  },
  {
    type: "market_pulse",
    label: "Market Pulse",
    description: "Real-time market summary",
    category: "market",
    defaultConfig: {},
  },
  {
    type: "sector_narratives",
    label: "Sector Narratives",
    description: "Hot narratives by sector",
    category: "market",
    defaultConfig: {},
  },

  // Charts
  {
    type: "token_metrics_card",
    label: "Token Metrics Card",
    description:
      "Mini dashboard for a single token with sparkline, price changes, fundamentals, and derivatives",
    category: "market",
    defaultConfig: {
      ticker: "BTC",
    },
    configHints: "ticker: uppercase token symbol (e.g. 'BTC', 'ETH', 'SOL')",
  },
  {
    type: "time_series_chart",
    label: "Multi-Token Chart",
    description: "Compare multiple tokens across metrics",
    category: "charts",
    defaultConfig: {
      tickers: ["BTC", "ETH"],
      metric: "price_close",
      timeframe: "30d",
      style: "line",
      show_legend: true,
      index_to_100: true,
    },
    configHints:
      "tickers: array of symbols; metric: 'price_close' | 'market_cap' | 'volume_24h' | 'tvl'; timeframe: '7d' | '30d' | '90d' | '365d' | 'All'",
  },
  {
    type: "bar_chart",
    label: "Bar Chart",
    description: "Compare metrics across tokens",
    category: "charts",
    defaultConfig: {
      metric: "market_cap",
      sort: "desc",
      limit: 10,
      color_by_value: false,
    },
  },
  {
    type: "scatter_chart",
    label: "Scatter Plot",
    description: "Visualize correlation between metrics",
    category: "charts",
    defaultConfig: {
      x_metric: "market_cap",
      y_metric: "volume_24h",
      show_labels: true,
    },
  },
  {
    type: "treemap",
    label: "Treemap",
    description: "Market cap weighted visualization",
    category: "charts",
    defaultConfig: {
      size_metric: "market_cap",
      color_metric: "price_close",
      limit: 30,
    },
  },
  {
    type: "heatmap",
    label: "Heatmap",
    description:
      "Time-series heatmap with transforms (momentum, vol, dominance)",
    category: "charts",
    defaultConfig: {
      metric: "price_close",
      time_range: "30d",
      transform: "raw",
      window_days: 7,
      clustered: false,
    },
    configHints:
      "metric: 'price_close' | 'market_cap' | 'volume_24h' | 'tvl' | 'futures_fr_avg'; time_range: '7d' | '30d' | '90d' | '365d'; transform: 'raw' | 'momentum' | 'vol' | 'dominance'; window_days: 7 | 30 | 90; clustered: boolean",
  },

  // Portfolio
  {
    type: "portfolio_nav",
    label: "Portfolio NAV",
    description: "Track portfolio value and performance vs BTC",
    category: "portfolio",
    defaultConfig: { timeframe: "all", show_benchmark: false },
    configHints:
      "timeframe: '1w' | '1m' | '3m' | '6m' | '1y' | 'all'; show_benchmark: boolean",
  },
  {
    type: "portfolio_holdings",
    label: "Holdings Table",
    description: "View current portfolio positions",
    category: "portfolio",
    defaultConfig: {},
  },
  {
    type: "portfolio_allocation",
    label: "Allocation",
    description: "Asset allocation breakdown",
    category: "portfolio",
    defaultConfig: {},
  },
  {
    type: "correlation_matrix",
    label: "Correlation Matrix",
    description: "Top tokens correlation matrix by returns",
    category: "charts",
    defaultConfig: { metric: "price_close", time_range: "7d" },
    configHints:
      "metric: 'price_close' | 'market_cap' | 'volume_24h' | 'tvl'; time_range: '7d' | '30d' | '90d' | '365d'",
  },
  {
    type: "portfolio_pulse",
    label: "Portfolio Pulse",
    description:
      "Quick overview with NAV, P&L sparkline, health metrics, and top gainers/losers from holdings",
    category: "portfolio",
    defaultConfig: { time_range: "7d" },
    configHints: "time_range: '24h' | '7d' | '30d'",
  },
  {
    type: "portfolio_metrics_overview",
    label: "Portfolio Metrics",
    description:
      "Categorized portfolio metrics including performance, risk, market exposure, and concentration stats",
    category: "portfolio",
    defaultConfig: {},
  },
  {
    type: "risk_alerts",
    label: "Threshold Monitor",
    description:
      "Monitor alert policies and their current status (triggered, warning, ok)",
    category: "portfolio",
    defaultConfig: {},
  },

  // Technical
  {
    type: "price_overview",
    label: "Price Overview",
    description: "Current price with technical indicators",
    category: "technical",
    defaultConfig: { ticker: "BTC", show_technicals: true },
  },
  {
    type: "support_resistance",
    label: "S/R Levels",
    description: "Key support and resistance levels",
    category: "technical",
    defaultConfig: { ticker: "BTC" },
  },
  {
    type: "tradingview_chart",
    label: "TradingView Chart",
    description: "Full TradingView chart embed",
    category: "technical",
    defaultConfig: { ticker: "BTC", interval: "D", theme: "dark" },
    configHints:
      "ticker: symbol; interval: '1' | '5' | '15' | '60' | 'D' | 'W'; theme: 'dark' | 'light'",
  },

  // Social
  {
    type: "tweet_feed",
    label: "Twitter Feed",
    description: "Filtered crypto tweets",
    category: "social",
    defaultConfig: {
      tickers: ["BTC", "ETH"],
      sentiment_filter: "all",
      limit: 20,
    },
    configHints:
      "tickers: array of symbols; sentiment_filter: 'all' | 'bullish' | 'bearish' | 'neutral'; limit: 10-50",
  },
  {
    type: "news_feed",
    label: "News Feed",
    description: "Latest crypto news",
    category: "social",
    defaultConfig: { limit: 20 },
  },
  {
    type: "youtube_alpha",
    label: "YouTube Alpha",
    description: "Recent crypto video content",
    category: "social",
    defaultConfig: {},
  },
  {
    type: "daily_recap",
    label: "24h AI Recap",
    description: "AI-powered 24-hour analysis of news and social sentiment",
    category: "social",
    defaultConfig: { tokens: [] },
    configHints: "tokens: array of token symbols (optional)",
  },

  // Derivatives
  {
    type: "funding_rate",
    label: "Funding Rate",
    description: "Historical funding rates with price overlay",
    category: "derivatives",
    defaultConfig: {
      tickers: ["BTC"],
      show_weighted: true,
      dashboard_type: "layer1",
    },
    configHints:
      "dashboard_type: sector code (e.g. 'layer1', 'dex', 'ai'); tickers: array of symbols (optional); show_weighted: boolean",
  },
  {
    type: "open_interest",
    label: "Open Interest",
    description: "Track OI trends with price overlay",
    category: "derivatives",
    defaultConfig: { ticker: "BTC", dashboard_type: "layer1" },
    configHints:
      "dashboard_type: sector code (e.g. 'layer1', 'dex', 'ai'); ticker: single symbol (optional)",
  },

  // BTC On-Chain & Cycle
  {
    type: "bull_market_indicators",
    label: "Bull Market Indicators",
    description: '"Sell at the top" indicator summary table',
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "ahr999",
    label: "AHR999 Index",
    description: "Bitcoin cycle indicator with buy/sell zones",
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "pi_cycle_top",
    label: "Pi Cycle Top",
    description: "MA crossover cycle top detection indicator",
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "puell_multiple",
    label: "Puell Multiple",
    description: "Mining profitability indicator for cycle tops/bottoms",
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "golden_ratio_multiplier",
    label: "BTC Golden Ratio",
    description: "Bitcoin Fibonacci-based long-term valuation zones",
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "stock_to_flow",
    label: "BTC Stock to Flow",
    description: "Bitcoin scarcity model with halving markers",
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "holder_realized_price",
    label: "BTC Realized Price",
    description: "Bitcoin cost basis for short/long-term holders",
    category: "onchain",
    defaultConfig: {},
  },
  {
    type: "global_m2",
    label: "Global M2 vs BTC",
    description: "Global M2 liquidity vs BTC price correlation",
    category: "onchain",
    defaultConfig: {},
  },

  // Macro
  {
    type: "fear_greed",
    label: "Fear & Greed",
    description: "Market sentiment index",
    category: "macro",
    defaultConfig: { show_gauge: true, show_history: false },
    configHints:
      "show_gauge: boolean; show_history: boolean; history_days: 7-90",
  },
  {
    type: "etf_flows",
    label: "ETF Flows",
    description: "Bitcoin ETF inflows/outflows",
    category: "macro",
    defaultConfig: { ticker: "btc" },
    configHints: "ticker: 'btc' | 'eth'",
  },
  {
    type: "fx_movers",
    label: "FX Strongest",
    description: "Strongest currencies vs USD",
    category: "macro",
    defaultConfig: { direction: "strongest" },
    configHints: "direction: 'strongest' | 'weakest'",
  },
  {
    type: "fx_movers_weakest",
    label: "FX Weakest",
    description: "Weakest currencies vs USD",
    category: "macro",
    defaultConfig: { direction: "weakest" },
  },
  {
    type: "commodities_movers",
    label: "Commodities Gainers",
    description: "Top gainers in commodities",
    category: "macro",
    defaultConfig: { direction: "gainers" },
    configHints: "direction: 'gainers' | 'losers'",
  },
  {
    type: "commodities_movers_losers",
    label: "Commodities Losers",
    description: "Top losers in commodities",
    category: "macro",
    defaultConfig: { direction: "losers" },
  },
  {
    type: "macro_key_metrics",
    label: "Macro Indicators",
    description: "DXY, VIX, yields, M2, and other macro indicators",
    category: "macro",
    defaultConfig: {},
  },
  {
    type: "fed_rate_probabilities",
    label: "Fed Rate Probabilities",
    description: "Market-implied Fed rate decision probabilities",
    category: "macro",
    defaultConfig: {},
  },
  {
    type: "economic_calendar",
    label: "Economic Calendar",
    description: "Upcoming and recent economic events",
    category: "macro",
    defaultConfig: {},
  },

  // Utility
  {
    type: "notepad",
    label: "Notepad",
    description: "Personal notes that auto-save to local storage",
    category: "utility",
    defaultConfig: { content: "" },
  },
  {
    type: "spreadsheet",
    label: "Spreadsheet",
    description: "Excel-like table with formulas and auto-save",
    category: "utility",
    defaultConfig: { data: [], rows: 12, cols: 8 },
  },

  // Thesis
  {
    type: "thesis",
    label: "Thesis Tracker",
    description: "Track and validate your investment thesis",
    category: "thesis",
    defaultConfig: {},
  },

  // Signals — Analytical Widgets
  {
    type: "spread_ratio",
    label: "Spread & Ratio",
    description:
      "Price ratio (X/Y) with log-price toggle. Ideal for long/short and pairs trading strategies to visualize the spread between two assets.",
    category: "signals",
    defaultConfig: { signal_name: "ratio" },
  },
  {
    type: "zscore_engine",
    label: "Z-Score Engine",
    description:
      "Z-score oscillator with ±2σ entry and ±3σ extreme bands. Essential for long/short strategies to identify mean-reversion entry and exit points.",
    category: "signals",
    defaultConfig: { signal_name: "z_score" },
  },
  {
    type: "stationarity",
    label: "Stationarity",
    description:
      "ADF p-value + Hurst exponent with trust thresholds. Use for long/short strategies to validate whether a pair is stationary enough for mean-reversion trading.",
    category: "signals",
    defaultConfig: { hurst_signal: "hurst", adf_signal: "sig_adf_pvalue" },
  },

  // Signals — Sparkline Widgets (auto-generated from SIGNAL_DEFINITIONS)
  // ...SIGNAL_DEFINITIONS.map((sig) => ({
  //   type: sig.widgetType,
  //   label: sig.label,
  //   description: sig.description,
  //   category: "signals" as WidgetCategory,
  //   defaultConfig: { signal_name: sig.signalName },
  // })),

  // ═══════════════════════════════════════════════════════════════════════════
  // TOKEN DASHBOARD WIDGETS (tw_*)
  // Per-token widgets — each independently configured with a ticker
  // ═══════════════════════════════════════════════════════════════════════════

  // — Overview —
  { type: "tw_key_stats", label: "Token Key Stats", description: "Key statistics for a token (market cap, volume, TVL, fees)", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol (e.g. 'BTC', 'ETH', 'SOL')" },
  { type: "tw_performance_strip", label: "Token Performance Strip", description: "Price performance across multiple time windows", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_risk_efficiency", label: "Token Risk & Efficiency", description: "Risk-adjusted performance metrics (Sharpe, Sortino, Calmar)", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_market_relationships", label: "Token Market Relationships", description: "Beta, correlation, and capture ratios vs BTC and ETH", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_sector_heat", label: "Token Sector Heat", description: "Sector heatmap context for a token", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_funding_trend", label: "Token Funding Trend", description: "Historical funding rate trend for a token", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_order_book", label: "Token Order Book", description: "Real-time Binance order book depth", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_supply_unlocks", label: "Token Supply Unlocks", description: "Token allocation and upcoming unlock schedule", category: "token_overview", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },

  // — Performance —
  { type: "tw_moving_averages", label: "Token Moving Averages", description: "SMA lines and MA distance indicators", category: "token_performance", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_vs_benchmarks", label: "Token vs Benchmarks", description: "Indexed returns vs BTC and ETH", category: "token_performance", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_performance_quality", label: "Token Performance Quality", description: "Alpha, capture ratios, and win rates vs benchmark", category: "token_performance", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_seasonal_performance", label: "Token Seasonal Performance", description: "Monthly, quarterly, and annual return heatmap", category: "token_performance", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_streaks_efficiency", label: "Token Streaks & Efficiency", description: "Win/loss streaks, CAGR, max drawdown, Calmar ratio", category: "token_performance", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },

  // — Risk —
  { type: "tw_rolling_volatility", label: "Token Rolling Volatility", description: "Rolling annualised volatility chart", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_sharpe_sortino", label: "Token Sharpe / Sortino", description: "Rolling Sharpe and Sortino ratio chart", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_var_es", label: "Token VaR & ES", description: "Rolling Value-at-Risk and Expected Shortfall", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_skew_kurtosis", label: "Token Skew & Kurtosis", description: "Rolling return distribution skewness and kurtosis", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_upside_downside", label: "Token Upside / Downside Deviation", description: "Rolling upside and downside deviation analysis", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_tail_metrics", label: "Token Tail Metrics", description: "Tail risk panel: VaR, ES, skew, kurtosis, crash sensitivity", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_beta_correlation", label: "Token Beta & Correlation", description: "Rolling beta, correlation, and upside/downside capture vs BTC/ETH", category: "token_risk", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },

  // — Valuation —
  { type: "tw_fees_dynamics", label: "Token Fees Dynamics", description: "Historical protocol fees time series", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_tvl_dynamics", label: "Token TVL Dynamics", description: "Total Value Locked history chart", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_dau_dynamics", label: "Token DAU Dynamics", description: "Daily Active Users history chart", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_mc_fees_history", label: "Token MC/Fees History", description: "Market Cap to Fees ratio over time", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_mc_tvl_history", label: "Token MC/TVL History", description: "Market Cap to TVL ratio over time", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_efficiency_panel", label: "Token Efficiency Panel", description: "Fee yield, Fees/TVL, and MC/Fees z-score", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_valuation_insights", label: "Token Valuation Insights", description: "Fee CV, MC/Fees momentum, and sector percentile ranks", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_valuation_ratios", label: "Token Valuation Ratios", description: "MC/TVL, MC/Fees, MC/DAU, TVL/Fees, and other key ratios", category: "token_valuation", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },

  // — Derivatives —
  { type: "tw_oi_summary", label: "Token OI Summary", description: "Open interest snapshot with change windows", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_oi_chart", label: "Token OI Chart", description: "Historical open interest chart", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_funding_summary", label: "Token Funding Summary", description: "Funding rate summary with period averages and extremes", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_weighted_funding_chart", label: "Token Weighted Funding Chart", description: "OI-weighted and volume-weighted funding rate history", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_exchange_funding", label: "Token Exchange Funding", description: "Funding rate breakdown by exchange", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_regime", label: "Token Funding Regime", description: "Current market regime based on funding and OI", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_funding_extremes", label: "Token Funding Extremes", description: "Historical funding rate extremes and distribution", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
  { type: "tw_funding_rate_chart", label: "Token Funding Rate Chart", description: "Funding rate history chart", category: "token_derivatives", defaultConfig: { ticker: "" }, configHints: "ticker: uppercase token symbol" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

export function getWidgetSchema(
  type: WidgetType,
): WidgetSchemaEntry | undefined {
  return WIDGET_SCHEMA.find((w) => w.type === type);
}

export function getWidgetSchemaByCategory(
  category: WidgetCategory,
): WidgetSchemaEntry[] {
  return WIDGET_SCHEMA.filter((w) => w.category === category);
}

/**
 * Get valid widget types as a set
 */
export function getValidWidgetTypes(): Set<string> {
  return new Set(WIDGET_SCHEMA.map((w) => w.type));
}

/**
 * Get all widget schemas - useful for backend sync or export
 */
export function getAllWidgetSchemas(): WidgetSchemaEntry[] {
  return [...WIDGET_SCHEMA];
}
